import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useCallback, useState } from "react";
import { ZegoExpressEngine } from "zego-express-engine-webrtc";
//import StageLinkManagerLayout from "../StageLinkMangerLayout_native";
import StageLinkManagerLayout from "../StageLinkMangerLayout_updated";

import TalentLiveStream from "./TalentLiveStream";
import { zegoInit } from "./../../../utils/zegoInit";
import { useStartEndStageSession } from "../../../services/stagemode_api/talent/talent_api";
import { enqueueSnackbar } from "notistack";
import { useCurrentTalentProfile } from "../../../hooks";
import useCurrentBusinessInfo from "../../../hooks/useCurrentBusiness";
import SocketHandler from "../../../utils/socketHandler_karokelyrics";

export function StageLinkTalentPage() {
  const { channel_name } = useParams();
  const navigate = useNavigate();
  // Change from useRef to useState to trigger re-renders
  const [zg, setZg] = useState<ZegoExpressEngine | null>(null);
  const [params] = useSearchParams();
  const userId = params.get("userId");
  const bookingId = params.get("bookingId");
  const { currentProfile } = useCurrentTalentProfile();
  const { currentBusiness } = useCurrentBusinessInfo();

  const startEndStageSession = useStartEndStageSession(currentProfile?.id);

  const [karaokeActive, setKaraokeActive]       = useState(false);
  const [karaokeTrackName, setKaraokeTrackName] = useState("");

  /* ── Incoming reactions ───────────────────────────────────── */
  const [flyingEmojis, setFlyingEmojis] = useState<
    { id: number; emoji: string; x: number }[]
  >([]);
  const emojiIdRef = useRef(0);

  const handleKaraokeChange = useCallback((active: boolean, track: string) => {
    setKaraokeActive(active);
    setKaraokeTrackName(active ? track : "");
  }, []);

  useEffect(() => {
    try {
      const zego = zegoInit();
      setZg(zego); // This will trigger a re-render
      console.log("[Zego] Engine initialized", zego);
    } catch (error) {
      console.error("[Zego] Init failed:", error);
    }
  }, [channel_name, userId]);

  /* ── Subscribe to host/audience reactions via Socket.IO ──── */
  useEffect(() => {
    if (!bookingId) return;
    const socketHandler = SocketHandler.getInstance();

    socketHandler.subscribeToReactions(bookingId, (data) => {
      if (!data?.emoji) return;
      const id = ++emojiIdRef.current;
      const x  = 10 + Math.random() * 70;
      setFlyingEmojis((prev) => [...prev, { id, emoji: data.emoji, x }]);
      setTimeout(() => {
        setFlyingEmojis((prev) => prev.filter((e) => e.id !== id));
      }, 2500);
    });

    return () => {
      socketHandler.unsubscribeFromReactions(bookingId);
    };
  }, [bookingId]);

  const handleEndStream = async () => {
    try {
      await startEndStageSession.mutateAsync({
        bookingId: bookingId!,
        status: "end",
        ...(currentBusiness?.id && {
          business_id: currentBusiness?.id.toString(),
        }),
      });
      navigate("/");
      enqueueSnackbar("Stream ended successfully", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar("Failed to end stream", {
        variant: "error",
      });
    }
  };

  const handleCallEnd = () => {
    navigate("/");
  };

  const sing4YouCallbacks = useMemo(
    () => userId ? { userId, onKaraokeChange: handleKaraokeChange } : null,
    [userId, handleKaraokeChange]
  );

  if (!userId) return <></>;

  return (
    <div style={{ position: "relative" }}>
      <StageLinkManagerLayout zg={zg} sing4YouCallbacks={sing4YouCallbacks}>
        <TalentLiveStream
          onCallEnd={handleCallEnd}
          onEndStream={handleEndStream}
          userId={userId ?? ""}
          videoId={channel_name!}
          zg={zg}
          karaokeActive={karaokeActive}
          karaokeTrackName={karaokeTrackName}
        />
      </StageLinkManagerLayout>

      {/* Flying emoji reactions — overlaid on top of the whole page */}
      {flyingEmojis.map(({ id, emoji, x }) => (
        <span
          key={id}
          style={{
            position: "fixed",
            bottom: "30%",
            left: `${x}%`,
            fontSize: "2.4rem",
            pointerEvents: "none",
            zIndex: 9999,
            animation: "flyEmojiUp 2.4s ease-out forwards",
            filter: "drop-shadow(0 0 8px rgba(229,9,20,0.5))",
            userSelect: "none",
          }}
        >
          {emoji}
        </span>
      ))}

      {/* Keyframe injection for Zego page (no sing4you-live.css here) */}
      <style>{`
        @keyframes flyEmojiUp {
          0%   { opacity: 1;   transform: translateY(0)      scale(1);   }
          60%  { opacity: 0.9; transform: translateY(-120px)  scale(1.25); }
          100% { opacity: 0;   transform: translateY(-210px)  scale(0.9); }
        }
      `}</style>
    </div>
  );
}
